import React, { FunctionComponent, useState } from "react";
import classNames from "classnames";
import InfyButton from "../../../InfyButton/InfyButton";
import { isInFrame } from "./autoCloseCountdown";
import useAutoCloseAfterSubmit from "./useAutoCloseAfterSubmit";

interface AutoCloseNoticeProps {
  className?: string;
}

/**
 * The "This tab will close in N seconds" note under a layout's result screen, with Close now and Stay on
 * this page. Render it only for a submission that has just succeeded — it starts counting when it mounts.
 *
 * Nothing renders inside a frame: a framed page cannot close the tab, and its host already hears
 * `formSubmitted`.
 */
const AutoCloseNotice: FunctionComponent<AutoCloseNoticeProps> = ({ className }) => {
  // Read once, on the client: the note is only ever mounted after a submit in the browser.
  const [offered] = useState(() => typeof window !== "undefined" && !isInFrame(window));
  const { phase, message, announcement, closeNow, stay } = useAutoCloseAfterSubmit(offered);

  if (!offered) return null;

  const counting = phase === "counting";

  return (
    <div className={classNames("mx-auto flex w-full max-w-md flex-col items-center gap-3 rounded-lg bg-neutral-3 p-3 text-center", className)}>
      {/* The ticking seconds are shown, not announced: inside the live region every one of them would be read out. */}
      {counting && (
        <p className="m-0 text-sm text-neutral-9" aria-hidden="true">
          {message}
        </p>
      )}
      {/* Always mounted so each change is announced: the start once (visually hidden while the seconds tick), then
          the outcome, shown in the same place. */}
      <p className={counting ? "sr-only" : "m-0 text-sm text-neutral-9"} role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
      {counting && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <InfyButton type="primary" bicolor="primary" size="small" onClick={closeNow}>
            Close now
          </InfyButton>
          <InfyButton type="ghost" bicolor="primary" size="small" onClick={stay}>
            Stay on this page
          </InfyButton>
        </div>
      )}
    </div>
  );
};

export default AutoCloseNotice;
